import React, { useState, useEffect } from 'react';
import { ArrowRight, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hover colors for each card
const hoverColors = [
  'hover:bg-orange-500 hover:text-white hover:border-orange-400',
  'hover:bg-teal-500 hover:text-white hover:border-teal-400',
  'hover:bg-purple-500 hover:text-white hover:border-purple-400',
  'hover:bg-blue-500 hover:text-white hover:border-blue-400',
  'hover:bg-rose-500 hover:text-white hover:border-rose-400',
  'hover:bg-amber-500 hover:text-white hover:border-amber-400',
  'hover:bg-emerald-500 hover:text-white hover:border-emerald-400',
  'hover:bg-indigo-500 hover:text-white hover:border-indigo-400',
  'hover:bg-cyan-500 hover:text-white hover:border-cyan-400',
  'hover:bg-pink-500 hover:text-white hover:border-pink-400',
  'hover:bg-lime-600 hover:text-white hover:border-lime-500',
  'hover:bg-red-500 hover:text-white hover:border-red-400',
];

// Icon colors (default state)
const iconColors = [
  'text-orange-500',
  'text-teal-500',
  'text-purple-500',
  'text-blue-500',
  'text-rose-500',
  'text-amber-500',
  'text-emerald-500',
  'text-indigo-500',
  'text-cyan-500',
  'text-pink-500',
  'text-lime-600',
  'text-red-500',
];

const BrowseCategories = ({ onCategorySelect, onViewAll, maxItems = 8 }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.filter(c => c.is_active !== false && c.name?.trim()).slice(0, maxItems));
        }
      } catch {}
    };
    fetchCategories();
  }, []);

  if (categories.length === 0) return null;

  return (
    <section className="py-10 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950" data-testid="browse-categories-section">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 text-sm font-medium rounded-full mb-3">
            Browse Categories
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
            Shop by Health Category
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-lg mx-auto">
            Explore our wide range of healthcare products organized by category for easy navigation
          </p>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {categories.map((cat, i) => (
            <button
              key={cat.id}
              onClick={() => onCategorySelect?.(cat.id, cat.slug)}
              className={`group relative flex flex-col items-center text-center p-5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl transition-all duration-300 hover:shadow-xl hover:scale-[1.03] hover:-translate-y-1 ${hoverColors[i % hoverColors.length]}`}
              data-testid={`browse-cat-${cat.slug}`}
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 transition-colors duration-300 ${iconColors[i % iconColors.length]} group-hover:text-white`}>
                {cat.image ? (
                  <img src={cat.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <Package className="w-7 h-7" />
                )}
              </div>

              {/* Name */}
              <h3 className="font-semibold text-sm text-gray-800 dark:text-white group-hover:text-white transition-colors duration-300 uppercase tracking-wide leading-tight">
                {cat.name}
              </h3>

              {/* Product Count */}
              <p className="text-xs text-gray-400 group-hover:text-white/80 transition-colors duration-300 mt-1">
                {cat.product_count || 0} Products
              </p>
            </button>
          ))}
        </div>

        {/* View All */}
        {onViewAll && (
          <div className="text-center mt-6">
            <button
              onClick={onViewAll}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors group"
              data-testid="view-all-categories-btn"
            >
              View All Categories
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BrowseCategories;
